/**
 * MarketplaceBreakdown Component - Enhanced marketplace-brand performance analysis
 * Fixed table header disappearing, column alignment, and commission rate accuracy
 * Now supports brand-marketplace pairs with proper commission overrides
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Card,
  Box,
  Text,
  BlockStack,
  InlineStack,
  Divider,
  IndexTable,
  Spinner,
  EmptyState,
  Pagination
} from '@shopify/polaris';
import { useMarketplaceAnalytics, useBrands } from '../../utils/queries';
import { DEFAULT_PAGE_SIZE, PageSizeSelect } from '../../utils/table';
import apiClient from '../../utils/api';

export default function MarketplaceBreakdown({
  brandId = 'all',
  period = 30,
  dateRange = null,
  title = 'Marketplace Performance',
  showFilters = true,
  compact = false,
  onMarketplaceSelect,
  ...props
}) {
  const [sortBy, setSortBy] = useState('netSales');
  const [sortDirection, setSortDirection] = useState('descending');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Build query params based on whether we have a date range or period
  const queryParams = useMemo(() => {
    const params = { brand: brandId };

    if (dateRange && dateRange.startDate && dateRange.endDate) {
      // Use custom date range
      params.startDate = dateRange.startDate;
      params.endDate = dateRange.endDate;
    } else {
      // Use period (days)
      params.days = period;
    }

    // Add sorting parameters for backend
    params.sortBy = sortBy;
    params.sortOrder = sortDirection === 'ascending' ? 'asc' : 'desc';

    return params;
  }, [brandId, period, dateRange, sortBy, sortDirection]);

  // A new brand, period, date range, SORT or row count starts again at page 1. Sorting belongs
  // in the reset for the same reason it does on the queue: the point of sorting by net sales is
  // to put the biggest rows on the first page, and staying on page 4 lands in the middle of
  // the new order.
  //
  // The date range is read down to its two STRINGS rather than depended on as the object, and
  // `queryParams` is deliberately not the dependency either: both callers rebuild the
  // `dateRange` prop inline on every render while a custom range is active, so an object
  // identity here would throw the reader back to page 1 on any unrelated re-render of the
  // page around this table.
  useEffect(() => {
    setPage(1);
  }, [brandId, period, dateRange?.startDate, dateRange?.endDate, sortBy, sortDirection, pageSize]);

  // Fetch both marketplace analytics data and brands data
  const marketplaceQuery = useMarketplaceAnalytics(queryParams);

  const brandsQuery = useBrands();

  const {
    data: marketplaceData = [],
    isLoading: marketplaceLoading = false,
    error: marketplaceError = null,
    refetch: refetchMarketplace = () => { },
  } = marketplaceQuery || {};

  const {
    data: brandsResponse = [],
    isLoading: brandsLoading = false,
  } = brandsQuery || {};

  // Robust brands data parsing
  const parseBrandsResponse = (response) => {
    try {
      if (!response) return [];

      // Handle direct array
      if (Array.isArray(response)) {
        return response.filter(brand => brand && typeof brand === 'object' && brand.id);
      }

      // Handle object with brands array
      if (typeof response === 'object') {
        const brands = response.brands || response.data || response.items || [];
        return Array.isArray(brands) ? brands.filter(brand => brand && typeof brand === 'object' && brand.id) : [];
      }

      return [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplaceBreakdown: Error parsing brands response:', error);
      }
      return [];
    }
  };

  const brands = parseBrandsResponse(brandsResponse);

  // Create brand lookup for quick access with error handling
  const brandLookup = useMemo(() => {
    try {
      const lookup = new Map();
      if (Array.isArray(brands)) {
        brands.forEach(brand => {
          if (brand && brand.id) {
            lookup.set(brand.id, brand);
          }
        });
      }
      return lookup;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplaceBreakdown: Error creating brand lookup:', error);
      }
      return new Map();
    }
  }, [brands]);

  // Process ONLY real API data - no demo fallback
  const processedData = useMemo(() => {
    // Drop the backend's own TOTAL row.
    //
    // The endpoint UNION ALLs a `summary_totals` row with marketplace = 'TOTAL' and sorts
    // it last. Left in, it rendered as an ordinary data row AND was included in the
    // totals row added below — so every money and count column read exactly DOUBLE the
    // true figure. AOV hid it, because numerator and denominator doubled together.
    //
    // Filtered here rather than removed from the SQL: other consumers may rely on that
    // row, and one place deciding what a row means is safer than two.
    return (marketplaceData ?? []).filter((r) => r?.marketplace !== 'TOTAL');
  }, [marketplaceData]);

  /**
   * The backend's own TOTAL row, kept aside rather than thrown away.
   *
   * It is still excluded from `processedData` above (leaving it in the table body double-counted
   * everything), but it is the authoritative footer: `summary_totals` in
   * analytics.controller.js:622-647 computes every column server-side, including the SQL-rounded
   * aov and netPayout. Recomputing those in the browser meant the footer and the API could
   * disagree by rounding, and nothing said which was right.
   */
  const apiTotals = useMemo(
    () => (marketplaceData ?? []).find((r) => r?.marketplace === 'TOTAL') || null,
    [marketplaceData]
  );

  /** Distinct retailers, for the subtitle. Rows are brand x marketplace PAIRS, so the row count
   *  is not a channel count - two brands on Macy's is one sales channel, not two. */
  const distinctMarketplaceCount = useMemo(
    () => new Set(processedData.map((r) => r?.marketplace).filter(Boolean)).size,
    [processedData]
  );

  /**
   * The page cut from the rows already in hand. This endpoint returns the whole breakdown in
   * one response (it sorts server-side but does not paginate), so slicing locally costs no
   * request — and the size is deliberately NOT sent as a `limit`, because the API's own TOTAL
   * row is computed over the whole period and a limited response would make it disagree with
   * the rows above it.
   *
   * CLAMPED: the query refetches on a period or brand change, so the array can shrink under a
   * page that was valid when it was chosen, and an out-of-range slice would render the "no
   * attributed orders" row over a period that has orders in it.
   */
  const totalPages = Math.max(1, Math.ceil(processedData.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visibleData = useMemo(
    () => processedData.slice((safePage - 1) * pageSize, safePage * pageSize),
    [processedData, safePage, pageSize]
  );

  // Index table resource state
  const resourceName = {
    singular: 'marketplace',
    plural: 'marketplaces',
  };

  // Handle sort changes - Updated to match standardized column order
  // Removed: Weight, Discounts, Commission Rate. Added: Items, AOV. Renamed: Gross Revenue → Sales
  const handleSort = useCallback((headingIndex, direction) => {
    // Map heading index to backend sort field - standardized columns
    const sortOptions = [
      'brandName',      // 0: Brand
      'marketplace',    // 1: Marketplace
      'orderCount',     // 2: Orders
      'totalItems',     // 3: Items
      'aov',            // 4: AOV
      'sales',          // 5: Sales
      'refunds',        // 6: Refunds
      'netSales',       // 7: Net Sales
      'marketplaceCommission', // 8: Marketplace Commission
      'twCommission',   // 9: 2W Comm.
      'shippingFees',   // 10: Shipping Fees
      'returnFees',     // 11: Return Fees
      'netPayout'       // 12: Net Payout
    ];

    setSortBy(sortOptions[headingIndex]);
    setSortDirection(direction);
  }, []);


  // Combined loading state
  const isLoading = marketplaceLoading || brandsLoading;

  // Table headings - Standardized column order
  // Removed: Weight, Discounts, Commission Rate. Added: Items, AOV. Renamed: Gross Revenue → Sales
  // All start-aligned. The wireframe left-aligns every column including the numerics,
  // and Orders Management already does the same — this table was the only one
  // right-aligning its figures, which made the two admin tables disagree with each
  // other as well as with the design.
  const tableHeadings = [
    // Tips are added inline rather than through headingsFor, because these indices are
    // load-bearing for sorting and must stay in this order.
    //
    // NOTE: for a tip to render on a SORTABLE column at all, sortToggleLabels must be
    // supplied — Polaris reads sortToggleLabels[index][direction] unguarded and otherwise
    // returns the bare sort button, silently discarding tooltipContent. See below.
    { title: 'Brand', alignment: 'start', tooltipContent: 'The 2W brand. A brand selling on three retailers has three rows.', tooltipWidth: 'wide' },
    { title: 'Marketplace', alignment: 'start', tooltipContent: 'Which marketplace the orders were attributed to, resolved from each order\u2019s tag. An order whose tag matches no pattern lands under Others at the wrong rate.', tooltipWidth: 'wide' },
    { title: 'Orders', alignment: 'start', tooltipContent: 'Orders in this period, attributed to this brand and marketplace.', tooltipWidth: 'wide' },
    { title: 'Items', alignment: 'start', tooltipContent: 'Units across those orders.', tooltipWidth: 'wide' },
    { title: 'AOV', alignment: 'start', tooltipContent: 'Average order value: sales divided by orders.', tooltipWidth: 'wide' },
    { title: 'Sales', alignment: 'start', tooltipContent: 'Gross sales LESS discounts. The Gross sales column on Orders Management is before discounts, so the same order can legitimately read differently on the two screens.', tooltipWidth: 'wide' },
    { title: 'Refunds', alignment: 'start', tooltipContent: 'Refunded amount in this period, whenever the original order was placed.', tooltipWidth: 'wide' },
    { title: 'Net Sales', alignment: 'start', tooltipContent: 'Sales less refunds. This is the base every commission below is calculated on.', tooltipWidth: 'wide' },
    { title: 'Marketplace Commission', alignment: 'start', tooltipContent: '2W\u2019s own calculation: the configured marketplace rate applied to net sales. It is not the retailer\u2019s reported deduction \u2014 that figure is on the order itself, under Retail Connect.', tooltipWidth: 'wide' },
    { title: '2W Commission', alignment: 'start', tooltipContent: '2W\u2019s own share, at the configured rate for this brand and marketplace.', tooltipWidth: 'wide' },
    { title: 'Shipping Fees', alignment: 'start', tooltipContent: 'What 2W charged for shipping, from the brand\u2019s configured rate table. Not a carrier cost.', tooltipWidth: 'wide' },
    { title: 'Return Fees', alignment: 'start', tooltipContent: 'What 2W charged for returns, from the same rate table.', tooltipWidth: 'wide' },
    { title: 'Net Payout', alignment: 'start', tooltipContent: 'Net sales less every fee above. This is 2W\u2019s own arithmetic, not a figure any marketplace reported.', tooltipWidth: 'wide' },
  ];

  // Simple row rendering - Standardized columns
  // Removed: Weight, Discounts, Commission Rate. Added: Items, AOV. Renamed: Gross Revenue → Sales
  const rowMarkup = useMemo(() => {
    if (!visibleData || visibleData.length === 0) {
      return null;
    }
    return visibleData.map((item, index) => (
      <IndexTable.Row
        id={item.id}
        key={item.id}
        position={index}
        // Only attach a click handler when the parent actually supplied one. This was
        // `onClick={() => onMarketplaceSelect?.(item)}`, passed unconditionally, so with no
        // handler the rows still rendered as clickable and did nothing when clicked.
        onClick={onMarketplaceSelect ? () => onMarketplaceSelect(item) : undefined}
      >
        {/* Brand column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium">
            {item.brandName || ''}
          </Text>
        </IndexTable.Cell>

        {/* Marketplace column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="semibold">
            {item.marketplace}
          </Text>
        </IndexTable.Cell>

        {/* Orders column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start">
            {(item.orderCount || 0).toLocaleString()}
          </Text>
        </IndexTable.Cell>

        {/* Items column (NEW) */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start">
            {(item.totalItems || 0).toLocaleString()}
          </Text>
        </IndexTable.Cell>

        {/* AOV column (NEW) */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start">
            {(() => {
              try {
                return apiClient.formatCurrency(item.aov || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Sales column (was Gross Revenue) */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start">
            {(() => {
              try {
                return apiClient.formatCurrency(item.sales || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Refunds column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start">
            {(() => {
              try {
                return apiClient.formatCurrency(item.refunds || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Net Sales column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start" fontWeight="medium">
            {(() => {
              try {
                return apiClient.formatCurrency(item.netSales || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* MKT Commission column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start" fontWeight="medium">
            {(() => {
              try {
                return apiClient.formatCurrency(item.marketplaceCommission || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* 2W Commission column.

            `??`, not `||`. A brand can be on 0% 2W commission — the whole charge sits on the
            marketplace leg — and `||` reads that legitimate zero as missing and falls through
            to the COMBINED commission, printing the marketplace figure again in this column.
            The footer sums the raw values and so read $0.00 while every row above it showed
            the full amount.

            The fallback itself is kept: an order predating the commission split carries no
            2W leg at all, and for those `commission` is the right thing to show. Nullish
            coalescing distinguishes "no split recorded" from "split recorded as zero". */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start" fontWeight="medium">
            {(() => {
              try {
                return apiClient.formatCurrency(item.twCommission ?? 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Shipping Fees column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start">
            {(() => {
              try {
                return apiClient.formatCurrency(item.shippingFees || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Return Fees column */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start">
            {(() => {
              try {
                return apiClient.formatCurrency(item.returnFees || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>

        {/* Net Payout column - final amount merchant gets */}
        <IndexTable.Cell>
          <Text variant="bodyMd" alignment="start" fontWeight="semibold">
            {(() => {
              try {
                return apiClient.formatCurrency(item.netPayout || 0);
              } catch (error) {
                return '$0.00';
              }
            })()}
          </Text>
        </IndexTable.Cell>
      </IndexTable.Row>
    ));
  }, [visibleData, onMarketplaceSelect]);

  /**
   * Totals row, as the design specifies.
   *
   * Every column here is additive across rows — each row is one brand-marketplace
   * pair, and no pair appears twice — so summing is meaningful. AOV is the exception
   * and is NOT summed: an average of averages is wrong, so it is recomputed from the
   * totals it derives from. Orders is a plain sum for the same reason the Product/SKU
   * tab could not use one: there, rows overlapped; here they do not.
   */
  const totals = useMemo(() => {
    if (!processedData || processedData.length === 0) return null;

    // Prefer the API's TOTAL row. Same numbers the rest of the platform bills on.
    if (apiTotals) {
      const num = (key) => (Number.isFinite(Number(apiTotals[key])) ? Number(apiTotals[key]) : 0);
      const orderCount = num('orderCount');
      return {
        orderCount,
        totalItems: num('totalItems'),
        sales: num('sales'),
        refunds: num('refunds'),
        netSales: num('netSales'),
        marketplaceCommission: num('marketplaceCommission'),
        twCommission: num('twCommission'),
        shippingFees: num('shippingFees'),
        returnFees: num('returnFees'),
        netPayout: num('netPayout'),
        // Null rather than 0 when there are no orders - an undefined average must not render
        // as "$0.00", which reads as a real figure.
        aov: orderCount > 0 ? num('aov') : null,
      };
    }

    // Fallback only when the response carried no TOTAL row. Summing is valid here because each
    // row is one brand-marketplace pair and no pair appears twice; AOV is the exception and is
    // recomputed rather than summed, since an average of averages is wrong.
    const sum = (key) => processedData.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
    const orderCount = sum('orderCount');
    const sales = sum('sales');
    return {
      orderCount,
      totalItems: sum('totalItems'),
      sales,
      refunds: sum('refunds'),
      netSales: sum('netSales'),
      marketplaceCommission: sum('marketplaceCommission'),
      twCommission: sum('twCommission'),
      shippingFees: sum('shippingFees'),
      returnFees: sum('returnFees'),
      netPayout: sum('netPayout'),
      aov: orderCount > 0 ? sales / orderCount : null,
    };
  }, [processedData, apiTotals]);

  // CONDITIONAL RETURNS - AFTER ALL HOOKS
  if (isLoading) {
    return (
      <Card {...props}>
        <Box padding="400">
          <BlockStack gap="400" align="center">
            <Spinner size="large" />
            <BlockStack gap="200" align="center">
              <Text variant="bodyLg">Loading marketplace data</Text>
              <Text variant="bodySm" tone="subdued">
                Analyzing performance across sales channels and brands
              </Text>
            </BlockStack>
          </BlockStack>
        </Box>
      </Card>
    );
  }

  // Error state
  if (marketplaceError) {
    return (
      <Card {...props}>
        <Box padding="400">
          <EmptyState
            heading="Unable to load marketplace data"
            content={marketplaceError.message || 'There was an error loading the marketplace breakdown. Please try refreshing the page.'}
            action={{
              content: 'Retry',
              onAction: refetchMarketplace,
            }}
            image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
          />
        </Box>
      </Card>
    );
  }

  // Empty state - improved messaging based on data availability
  // The message stays; the early RETURN is gone.
  //
  // This block used to return a whole-card EmptyState before the table rendered, which made
  // the table's own empty row unreachable — so this was the one table on the console that
  // still lost its column headings when it had nothing to show, and an admin landing on an
  // empty period could not tell what the screen would contain. Now the same wording renders
  // inside the table, below the headings.
  // Only the two reachable branches. `isLoading` and `marketplaceError` both return
  // earlier (above), so an error message and a "Loading..." message here would be dead —
  // the block this replaced carried both because it ran in place of those returns, not
  // after them.
  const emptyMessage = Array.isArray(marketplaceData) && marketplaceData.length === 0
    ? 'No attributed orders in this period. Each row will be one brand on one marketplace, with what it sold and what was billed on it.'
    : 'Marketplace performance will appear here once orders are processed and tagged with a marketplace.';

  return (
    <Card {...props}>
      <Box padding="0">
        {/* Renders the `title` PROP, which this header used to ignore.
        
            It hardcoded "Cross-Brand Marketplace Analytics" while the component declared
            `title = 'Marketplace Performance'` as a prop and both callers passed one. So the
            prop was dead, and a single brand's Analytics tab announced a cross-brand table
            while showing exactly one brand — every row the same name. */}
        <Box padding="200" paddingBlockEnd="200">
          <BlockStack gap="050">
            <Text variant="headingMd">
              {title}
            </Text>
            <Text variant="bodySm" tone="subdued">
              {/* Distinct retailers, not the row count - each row is a brand x marketplace pair,
                  so "5 sales channels" for two brands across three retailers was wrong. */}
              Performance breakdown across {distinctMarketplaceCount} sales channel{distinctMarketplaceCount === 1 ? '' : 's'} ({processedData.length} brand–retailer row{processedData.length === 1 ? '' : 's'}) • Click column headers to sort
            </Text>
          </BlockStack>
        </Box>

        <Divider />

        {/* Enhanced Table with proper configuration */}
        <Box padding="0">
          <IndexTable
            resourceName={resourceName}
            itemCount={visibleData.length || 1}
            selectable={false}
            headings={tableHeadings}
            onSort={handleSort}
            sortable={tableHeadings.map(() => true)}
            // REQUIRED, or every tooltipContent above is silently dropped: Polaris reads
            // sortToggleLabels[index][direction] unguarded while building a sortable heading
            // and returns the bare sort button when the prop is absent (IndexTable.js:477).
            sortToggleLabels={tableHeadings.reduce((acc, h, i) => {
              acc[i] = i <= 1
                ? { ascending: 'A-Z', descending: 'Z-A' }
                : { ascending: 'Lowest first', descending: 'Highest first' };
              return acc;
            }, {})}
            sortDirection={sortDirection}
            sortColumnIndex={(() => {
              // Map backend sort field to column index - standardized columns
              const mapping = {
                'brandName': 0,
                'marketplace': 1,
                'orderCount': 2,
                'totalItems': 3,
                'aov': 4,
                'sales': 5,
                'refunds': 6,
                'netSales': 7,
                'marketplaceCommission': 8,
                'twCommission': 9,
                'shippingFees': 10,
                'returnFees': 11,
                'netPayout': 12
              };
              return mapping[sortBy] !== undefined ? mapping[sortBy] : 7; // Default to Net Sales
            })()}
            hasMoreItems={false}
            condensed={compact}
          >
            {visibleData.length === 0 ? (
              <IndexTable.Row id="empty-breakdown" position={0}>
                <IndexTable.Cell colSpan={tableHeadings.length}>
                  <Box padding="500">
                    <Text alignment="center" tone="subdued" variant="bodySm">
                      {emptyMessage}
                    </Text>
                  </Box>
                </IndexTable.Cell>
              </IndexTable.Row>
            ) : rowMarkup}
            {/* The TOTAL row is NOT part of the page.
                It is rendered after whatever slice is on screen, on every page, and it is
                summed over the WHOLE period — `totals` reads the API's own TOTAL row, or
                falls back to summing `processedData`, never `visibleData`. A total that
                vanished on page 2, or that only added up the ten rows in view, would be
                worse than no total at all. It is also excluded from `itemCount`, exactly as
                it was before paging, so it is never counted as a data row. */}
            {totals && (
              <IndexTable.Row id="totals" key="totals" position={visibleData.length} disabled>
                <IndexTable.Cell />
                <IndexTable.Cell>
                  <Text variant="bodyMd" fontWeight="bold">TOTAL</Text>
                </IndexTable.Cell>
                {[
                  ['orderCount', 'plain'], ['totalItems', 'plain'], ['aov', 'money'],
                  ['sales', 'money'], ['refunds', 'money'], ['netSales', 'money'],
                  ['marketplaceCommission', 'money'], ['twCommission', 'money'],
                  ['shippingFees', 'money'], ['returnFees', 'money'], ['netPayout', 'money'],
                ].map(([key, kind]) => (
                  <IndexTable.Cell key={key}>
                    <Text variant="bodyMd" fontWeight="bold">
                      {totals[key] == null
                        ? '—'
                        : kind === 'money'
                          ? apiClient.formatCurrency(totals[key])
                          : totals[key]}
                    </Text>
                  </IndexTable.Cell>
                ))}
              </IndexTable.Row>
            )}
          </IndexTable>
          {/* Both controls in one row, and only once there are rows — a pager under an empty
              table is three dead words. The pager drops out when everything fits; the row
              count stays, since it is how someone gets a second page in the first place.
              Labelled with `safePage`, the page the rows were actually sliced with. */}
          {processedData.length > 0 && (
            <Box padding="300">
              <InlineStack align="space-between" blockAlign="center">
                <PageSizeSelect value={pageSize} onChange={setPageSize} />
                {totalPages > 1 && (
                  <Pagination
                    hasPrevious={safePage > 1}
                    onPrevious={() => setPage(safePage - 1)}
                    hasNext={safePage < totalPages}
                    onNext={() => setPage(safePage + 1)}
                    label={`Page ${safePage} of ${totalPages}`}
                  />
                )}
              </InlineStack>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}